import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useFiscalYear } from '../contexts/FiscalYearContext';
import PageHeader from '../components/PageHeader';
import api from '../utils/api';
import * as XLSX from 'xlsx';
import { formatDateDMY } from '../utils/dateFormat';
import DateInputDMY from '../components/DateInputDMY';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

// Import activity components
import ActivityStats from '../components/activities/ActivityStats';
import ActivityFilters from '../components/activities/ActivityFilters';
import ActivitySearchBar from '../components/activities/ActivitySearchBar';
import ActivityPerformanceChart from '../components/activities/ActivityPerformanceChart';
import ActivityForm from '../components/activities/ActivityForm';
import PhotoCapture from '../components/PhotoCapture';
import ActivityStatusModal from '../components/activities/ActivityStatusModal';
import ActivityProgressModal from '../components/activities/ActivityProgressModal';
import { hasPermission } from '../utils/permissions';
import SearchableSelect from '@/components/ui/SearchableSelect';

const Activities = () => {
  const { user: currentUser } = useAuth();
  // Import calculateTotal from useCurrency
  const { formatAmount, getTaxBreakdown, calculateTotal, companySettings } = useCurrency();
  const { selectedFy } = useFiscalYear();
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Extra filters (assigned-to, activity_type, date range). Status stays on
  // the original chip row above so the page keeps the muscle-memory layout.
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  const [filterActivityType, setFilterActivityType] = useState('all');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  // Sync global Fiscal Year selection → list date filter.
  useEffect(() => {
    if (!selectedFy) return;
    setFilterFromDate(selectedFy.start_date);
    setFilterToDate(selectedFy.end_date);
  }, [selectedFy?.id]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditAssignmentModal, setShowEditAssignmentModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [statusUpdateNote, setStatusUpdateNote] = useState('');
  const [editAssignedTo, setEditAssignedTo] = useState('');
  const [editCreatedDate, setEditCreatedDate] = useState('');
  const supportUsers = useMemo(() => users.filter(u => u.role === 'support'), [users]);
  const [completionData, setCompletionData] = useState({
    invoice_number: '',
    work_order_no: '',
    total_amount: '',
    next_maintenance_date: ''
  });
  const [progressUpdate, setProgressUpdate] = useState({ update: '' });
  const [newActivity, setNewActivity] = useState({
    title: '',
    description: '',
    assigned_to: '',
    customer_id: '',
    product_id: '',
    serial_number: '',
    status: 'pending',
    activity_type: 'others',
    support_staff: '',
    due_date: '',
    created_date: new Date().toISOString().slice(0, 10),
    invoice_number: '',
    work_order_no: '',
    total_amount: '',
    next_maintenance_date: '',
    photos: []  // array of base64 dataUrl strings — captured via PhotoCapture
  });

  const fetchData = async () => {
    try {
      // Build query parameters
      let activityUrl = '/activities';
      const params = [];
      if (filterStatus !== 'all') params.push(`status=${filterStatus}`);
      if (searchQuery.trim()) params.push(`search=${encodeURIComponent(searchQuery)}`);
      if (params.length > 0) activityUrl += '?' + params.join('&');
      
      const [activitiesRes, usersRes, customersRes, productsRes] = await Promise.all([
        api.get(activityUrl),
        api.get('/users'),
        api.get('/customers'),
        api.get('/products')
      ]);
      setActivities(activitiesRes.data);
      setUsers(usersRes.data);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, searchQuery]);

  const handleAddActivity = async (e) => {
    e.preventDefault();
    try {
      // Clean up data - convert empty strings to null for number fields
      const submitData = { ...newActivity };
      
      // Convert empty strings to null for optional fields
      if (submitData.total_amount === '' || !submitData.total_amount) {
        delete submitData.total_amount;
      } else {
        submitData.total_amount = parseFloat(submitData.total_amount);
      }
      if (submitData.invoice_number === '') {
        delete submitData.invoice_number;
      }
      if (submitData.work_order_no === '') {
        delete submitData.work_order_no;
      }
      if (submitData.support_staff === '') {
        delete submitData.support_staff;
      }
      if (submitData.due_date === '') {
        delete submitData.due_date;
      }
      if (submitData.next_maintenance_date === '') {
        delete submitData.next_maintenance_date;
      }
      if (submitData.product_id === '') {
        delete submitData.product_id;
      }
      if (submitData.serial_number === '') {
        delete submitData.serial_number;
      }
      if (submitData.customer_id === '') {
        delete submitData.customer_id;
      }

      // Map the form's `created_date` (YYYY-MM-DD) to the backend's
      // `created_at` (full ISO datetime). Defaults to today on the form so
      // most users won't touch it; power users can back-date a visit.
      if (submitData.created_date) {
        submitData.created_at = new Date(submitData.created_date + 'T00:00:00').toISOString();
      }
      delete submitData.created_date;

      const response = await api.post('/activities', submitData);
      
      // Update product's serial number with next_maintenance_date
      if (submitData.product_id && submitData.serial_number && submitData.next_maintenance_date) {
        try {
          const product = products.find(p => p.id === submitData.product_id);
          if (product && product.serial_numbers) {
            const updatedSerials = product.serial_numbers.map(serial => {
              if (serial.serial_number === submitData.serial_number) {
                return {
                  ...serial,
                  next_maintenance_date: submitData.next_maintenance_date
                };
              }
              return serial;
            });
            
            await api.put(`/products/${product.id}`, {
              serial_numbers: updatedSerials
            });
          }
        } catch (updateError) {
          console.error('Error updating product maintenance date:', updateError);
          // Don't fail the activity creation if product update fails
        }
      }
      
      alert('Activity created successfully!');
      setNewActivity({ 
        title: '', 
        description: '', 
        assigned_to: '', 
        customer_id: '', 
        product_id: '',
        serial_number: '',
        status: 'pending',
        activity_type: 'others',
        support_staff: '',
        due_date: '',
        created_date: new Date().toISOString().slice(0, 10),
        invoice_number: '',
        work_order_no: '',
        total_amount: '',
        next_maintenance_date: '',
        photos: []
      });
      setShowAddForm(false);
      fetchData();
    } catch (error) {
      console.error('Full error object:', error);
      console.error('Error response:', error.response);
      
      // Better error message extraction
      let errorMessage = 'Error creating activity';
      if (error.response) {
        // Server responded with error
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data?.detail) {
          if (Array.isArray(error.response.data.detail)) {
            // Pydantic validation errors
            errorMessage = error.response.data.detail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
          } else {
            errorMessage = error.response.data.detail;
          }
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = `Error: ${error.response.status} - ${error.response.statusText}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert('Error creating activity: ' + errorMessage);
    }
  };

  const handleUpdateStatus = async (activityId, newStatus) => {
    setSelectedActivity({ id: activityId, newStatus });
    setShowStatusModal(true);
  };

  const handleConfirmStatusUpdate = async () => {
    if (!statusUpdateNote.trim()) {
      alert('Please enter details about this status update');
      return;
    }

    try {
      const updateData = {
        status: selectedActivity.newStatus,
        notes: statusUpdateNote
      };
      
      // Add invoice, amount, and next maintenance date if completing
      if (selectedActivity.newStatus === 'completed') {
        if (completionData.work_order_no) {
          updateData.work_order_no = completionData.work_order_no;
        }
        if (completionData.invoice_number) {
          updateData.invoice_number = completionData.invoice_number;
        }
        if (completionData.total_amount) {
          updateData.total_amount = parseFloat(completionData.total_amount);
        }
        if (completionData.next_maintenance_date) {
          updateData.next_maintenance_date = completionData.next_maintenance_date;
        }
      }
      
      await api.put(`/activities/${selectedActivity.id}`, updateData);
      
      // Update product's serial number with next_maintenance_date if provided
      if (selectedActivity.newStatus === 'completed' && 
          completionData.next_maintenance_date && 
          selectedActivity.product_id && 
          selectedActivity.serial_number) {
        try {
          const product = products.find(p => p.id === selectedActivity.product_id);
          if (product && product.serial_numbers) {
            const updatedSerials = product.serial_numbers.map(serial => {
              if (serial.serial_number === selectedActivity.serial_number) {
                return {
                  ...serial,
                  next_maintenance_date: completionData.next_maintenance_date
                };
              }
              return serial;
            });
            
            await api.put(`/products/${product.id}`, {
              serial_numbers: updatedSerials
            });
          }
        } catch (updateError) {
          console.error('Error updating product maintenance date:', updateError);
          // Don't fail the activity update if product update fails
        }
      }
      
      alert('Activity status updated successfully!');
      setShowStatusModal(false);
      setStatusUpdateNote('');
      setCompletionData({ invoice_number: '', work_order_no: '', total_amount: '', next_maintenance_date: '' });
      setSelectedActivity(null);
      fetchData();
    } catch (error) {
      alert('Error updating status: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddProgressUpdate = (activityId) => {
    setSelectedActivity({ id: activityId });
    setShowProgressModal(true);
  };

  const handleConfirmProgressUpdate = async () => {
    if (!progressUpdate.update.trim()) {
      alert('Please enter progress details');
      return;
    }

    try {
      await api.post(`/activities/${selectedActivity.id}/progress`, {
        update: progressUpdate.update,
        timestamp: new Date().toISOString()
      });
      alert('Progress update added successfully!');
      setShowProgressModal(false);
      setProgressUpdate({ update: '' });
      setSelectedActivity(null);
      fetchData();
    } catch (error) {
      alert('Error adding progress: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;
    
    try {
      await api.delete(`/activities/${activityId}`);
      alert('Activity deleted successfully!');
      fetchData();
    } catch (error) {
      alert('Error deleting activity: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditAssignment = (activity) => {
    setSelectedActivity(activity);
    setEditAssignedTo(activity.assigned_to || '');
    // Seed the date input from the activity's existing created_at (or due_date
    // as a fallback when older records have no created_at).
    const seed = activity.created_at || activity.due_date || '';
    setEditCreatedDate(seed ? String(seed).slice(0, 10) : '');
    setShowEditAssignmentModal(true);
  };

  const handleUpdateAssignment = async () => {
    try {
      const payload = { assigned_to: editAssignedTo };
      // Only send created_at if the user actually changed it (and it's not empty).
      const originalDate = String(selectedActivity.created_at || '').slice(0, 10);
      if (editCreatedDate && editCreatedDate !== originalDate) {
        payload.created_at = new Date(editCreatedDate + 'T00:00:00').toISOString();
      }
      await api.put(`/activities/${selectedActivity.id}`, payload);

      // Update local state
      setActivities(activities.map(a =>
        a.id === selectedActivity.id
          ? { ...a, assigned_to: editAssignedTo, ...(payload.created_at ? { created_at: payload.created_at } : {}) }
          : a
      ));

      setShowEditAssignmentModal(false);
      setShowDetailModal(false);
      alert('Activity updated successfully!');
      fetchData();
    } catch (error) {
      console.error('Error updating activity:', error);
      alert('Failed to update activity');
    }
  };

  const openDetailModal = (activity) => {
    setSelectedActivity(activity);
    setShowDetailModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const handleStatusChange = (activity, newStatus) => {
    setSelectedActivity({ ...activity, newStatus });
    setShowStatusModal(true);
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unassigned';
  };

  // Calculate statistics
  const totalActivitiesByUser = activities.length;
  const totalValue = activities
    .filter(act => act.status === 'completed' && act.total_amount)
    .reduce((sum, act) => sum + (parseFloat(act.total_amount) || 0), 0);

  // Count completed activities
  const completedActivitiesCount = activities.filter(act => act.status === 'completed').length;

  const filteredActivities = activities.filter(activity => {
    // Assigned-to filter
    if (filterAssignedTo !== 'all' && activity.assigned_to !== filterAssignedTo) return false;
    // Activity type filter
    if (filterActivityType !== 'all' && (activity.activity_type || 'others') !== filterActivityType) return false;
    // Date range filter (on created_at)
    if (filterFromDate || filterToDate) {
      const created = activity.created_at ? new Date(activity.created_at) : null;
      if (!created) return false;
      if (filterFromDate && created < new Date(filterFromDate + 'T00:00:00')) return false;
      if (filterToDate && created > new Date(filterToDate + 'T23:59:59')) return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const assignedUserName = getUserName(activity.assigned_to).toLowerCase();
    const createdByUserName = getUserName(activity.created_by).toLowerCase();
    const customerName = customers.find(c => c.id === activity.customer_id)?.name?.toLowerCase() || '';
    const serialNumber = activity.serial_number?.toLowerCase() || '';
    const invoiceNumber = activity.invoice_number?.toLowerCase() || '';
    const workOrderNumber = activity.work_order_no?.toLowerCase() || '';
    
    return (
      activity.title?.toLowerCase().includes(query) ||
      activity.description?.toLowerCase().includes(query) ||
      assignedUserName.includes(query) ||
      createdByUserName.includes(query) ||
      customerName.includes(query) ||
      serialNumber.includes(query) ||
      invoiceNumber.includes(query) ||
      workOrderNumber.includes(query)
    );
  }).sort((a, b) => {
    // Sort by created_at descending (newest first)
    const dateA = new Date(a.created_at || a.updated_at || 0);
    const dateB = new Date(b.created_at || b.updated_at || 0);
    return dateB - dateA;
  });

  // Distinct activity_type values seen in the dataset — keeps the filter
  // dropdown self-curating regardless of which types exist in the company.
  const activityTypeOptions = Array.from(new Set(activities.map(a => a.activity_type || 'others'))).sort();

  // Assignee options — limit to users actually assigned to something so the
  // dropdown stays short and meaningful.
  const assigneeIds = Array.from(new Set(activities.map(a => a.assigned_to).filter(Boolean)));
  const assigneeOptions = assigneeIds.map(id => ({ id, name: getUserName(id) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const advancedFilterCount =
    (filterAssignedTo !== 'all' ? 1 : 0) +
    (filterActivityType !== 'all' ? 1 : 0) +
    (filterFromDate ? 1 : 0) +
    (filterToDate ? 1 : 0);

  const resetAdvancedFilters = () => {
    setFilterAssignedTo('all');
    setFilterActivityType('all');
    setFilterFromDate('');
    setFilterToDate('');
  };

  const exportActivitiesExcel = () => {
    if (filteredActivities.length === 0) {
      alert('No activities to export with the current filters.');
      return;
    }
    const rows = filteredActivities.map((a) => ({
      'Title': a.title || '',
      'Activity Type': a.activity_type || '',
      'Status': (a.status || '').replace(/_/g, ' '),
      'Customer': customers.find(c => c.id === a.customer_id)?.name || '',
      'Assigned To': getUserName(a.assigned_to),
      'Created By': getUserName(a.created_by),
      'Due Date': a.due_date ? formatDateDMY(a.due_date) : '',
      'Created At': a.created_at ? formatDateDMY(a.created_at) : '',
      'Serial Number': a.serial_number || '',
      'Invoice Number': a.invoice_number || '',
      'Work Order No': a.work_order_no || '',
      'Total Amount': a.total_amount || '',
      'Next Maintenance': a.next_maintenance_date ? formatDateDMY(a.next_maintenance_date) : '',
      'Description': a.description || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Activities');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `activities_${stamp}.xlsx`);
  };

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  // Users who see all records (admins, or anyone with view_scope='all') can
  // edit any record (provided they have edit permission). Others may only
  // edit records they own/are assigned to.
  const seesAllRecords = isAdmin || (currentUser?.view_scope || 'all') === 'all';
  // Permission-aware: admins are always allowed; everyone else needs explicit
  // permissions.activities.<action>. Falls back to role defaults via
  // ROLE_DEFAULT_PERMISSIONS when the user has no custom matrix.
  const canCreateActivity = hasPermission(currentUser, 'activities', 'create');
  const canEditActivity = hasPermission(currentUser, 'activities', 'edit');
  const canDeleteActivity = hasPermission(currentUser, 'activities', 'delete');
  const canViewPerformanceChart = hasPermission(currentUser, 'activities', 'view');

  return (
    <div className="w-screen max-w-full overflow-x-hidden overflow-y-auto">
      {/* Activities page - No PageHeader on mobile */}
      <div className="w-full px-2 sm:px-4 lg:px-6 py-3 sm:py-6 overflow-x-hidden max-w-full">
        {/* Desktop only header */}
        <div className="hidden lg:block mb-4">
          <PageHeader title="📋 Activities Management">
            <Button onClick={() => window.location.href = '/dashboard'} variant="outline">
              Back to Dashboard
            </Button>
          </PageHeader>
        </div>
        {/* Statistics Cards */}
        <ActivityStats 
          totalActivities={totalActivitiesByUser}
          completedActivities={completedActivitiesCount}
          totalValue={totalValue}
          formatAmount={formatAmount}
        />

        <ActivityFilters
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          canCreateActivity={canCreateActivity}
          setShowAddForm={setShowAddForm}
          showAddForm={showAddForm}
          currentUser={currentUser}
        />

        {/* Advanced filters: assignee + type + date range + Excel export */}
        <div className="bg-white rounded-lg shadow p-3 mb-4 grid grid-cols-2 lg:grid-cols-5 gap-2" data-testid="activities-advanced-filters">
          <select
            value={filterAssignedTo}
            onChange={(e) => setFilterAssignedTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            data-testid="activities-filter-assignee"
          >
            <option value="all">All assignees</option>
            {assigneeOptions.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>

          <select
            value={filterActivityType}
            onChange={(e) => setFilterActivityType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            data-testid="activities-filter-type"
          >
            <option value="all">All types</option>
            {activityTypeOptions.map((t) => (
              <option key={t} value={t}>{(t || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
            ))}
          </select>

          <DateInputDMY
            value={filterFromDate}
            onChange={(e) => setFilterFromDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="From date"
            data-testid="activities-filter-from-date"
          />
          <DateInputDMY
            value={filterToDate}
            onChange={(e) => setFilterToDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="To date"
            data-testid="activities-filter-to-date"
          />
          <div className="flex gap-2">
            {advancedFilterCount > 0 && (
              <button
                type="button"
                onClick={resetAdvancedFilters}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                data-testid="activities-clear-filters-btn"
                title="Clear advanced filters"
              >
                Clear filters
              </button>
            )}
            <button
              type="button"
              onClick={exportActivitiesExcel}
              className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium whitespace-nowrap"
              data-testid="activities-export-excel-btn"
              title="Export filtered list to Excel"
            >
              ⬇ Export Excel
            </button>
          </div>
        </div>

        {/* Main Search Bar */}
        <ActivitySearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          resultsCount={filteredActivities.length}
        />

        {/* Filtered totals strip */}
        {filteredActivities.length > 0 && (() => {
          const totalAmount = filteredActivities.reduce((s, a) => s + (a.total_amount || 0), 0);
          const byStatus = filteredActivities.reduce((acc, a) => {
            const s = a.status || 'pending';
            acc[s] = (acc[s] || 0) + 1;
            return acc;
          }, {});
          return (
            <div className="flex flex-wrap items-center gap-3 mb-4 px-3 py-2 rounded-md bg-blue-50 border border-blue-200" data-testid="activities-filtered-totals">
              <span className="text-xs text-gray-700">
                Filtered: <b>{filteredActivities.length}</b> activities
              </span>
              {Object.entries(byStatus).map(([s, c]) => (
                <span key={s} className="text-xs px-2 py-1 rounded bg-white border border-blue-200 capitalize">
                  {s.replace(/_/g, ' ')}: <b>{c}</b>
                </span>
              ))}
              {totalAmount > 0 && (
                <span className="text-xs px-2 py-1 rounded bg-white border border-emerald-200 text-emerald-800" data-testid="activities-filtered-amount">
                  Total Amount: <b>{formatAmount(totalAmount)}</b>
                </span>
              )}
            </div>
          );
        })()}

        {/* Performance Chart - admins, support, data_entry (and anyone with full view) */}
        {canViewPerformanceChart && (
          <ActivityPerformanceChart
            activities={filteredActivities}
            getUserName={getUserName}
            formatAmount={formatAmount}
            currentUserRole={currentUser?.role}
          />
        )}

        {/* Activities List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading activities...</p>
            </div>
          ) : filteredActivities.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No activities found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery ? 'Try adjusting your search' : 'Get started by creating a new activity'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredActivities.map((activity) => (
              <Card key={activity.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{activity.title}</h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-2">
                        <span>👤 Created by: <strong className="text-blue-600">{getUserName(activity.created_by)}</strong></span>
                        <span>📋 Assigned to: <strong className="text-purple-600">{getUserName(activity.assigned_to)}</strong></span>
                        {activity.due_date && (
                          <span>📅 Due: {formatDateDMY(activity.due_date)}</span>
                        )}
                        {activity.activity_type && (
                          <span>🏷️ Type: <strong>{activity.activity_type}</strong></span>
                        )}
                      </div>
                      {activity.description && (
                        <p className="text-gray-600 text-sm mt-2">{activity.description}</p>
                      )}
                      {activity.customer_id && (
                        <p className="text-sm text-gray-600 mt-2">
                          🏢 Customer: <strong className="text-cyan-600">{customers.find(c => c.id === activity.customer_id)?.name}</strong>
                        </p>
                      )}
                      
                      {/* Financial Details - Show for completed activities */}
                      {activity.status === 'completed' && (activity.invoice_number || activity.work_order_no || activity.total_amount) && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                            {activity.invoice_number && (
                              <div>
                                <span className="text-gray-600">📄 Invoice #:</span>
                                <span className="ml-2 font-semibold text-green-700">{activity.invoice_number}</span>
                              </div>
                            )}
                            {activity.work_order_no && (
                              <div>
                                <span className="text-gray-600">🔧 Work Order #:</span>
                                <span className="ml-2 font-semibold text-green-700">{activity.work_order_no}</span>
                              </div>
                            )}
                            {activity.total_amount && (
                              <div className="bg-green-50 px-3 py-2 rounded-lg">
                                <div className="text-sm space-y-1">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Base Amount:</span>
                                    <span className="font-semibold text-gray-900">{formatAmount(parseFloat(activity.total_amount))}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Tax ({companySettings?.tax_percentage || 0}%):</span>
                                    <span className="font-semibold text-green-600">
                                      {formatAmount(getTaxBreakdown(parseFloat(activity.total_amount)).taxAmount)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between pt-1 border-t border-green-200">
                                    <span className="text-gray-700 font-bold">Total Value:</span>
                                    <span className="font-bold text-green-700">
                                      {formatAmount(getTaxBreakdown(parseFloat(activity.total_amount)).total)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <Badge className={getStatusBadge(activity.status)}>
                        {activity.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedActivity(activity);
                        setShowDetailModal(true);
                      }}
                      className="bg-gradient-to-r from-blue-700 to-green-700 hover:from-orange-600 hover:to-sky-600"
                      data-testid="view-activity-details-btn"
                    >
                      👁️ View Details
                    </Button>
                    {/* Edit gating now uses the per-user permissions matrix.
                        Non-admins still need to be the creator/assignee unless
                        they have full edit rights (admin/super_admin bypass). */}
                    {canEditActivity && (seesAllRecords || activity.created_by === currentUser?.id || activity.assigned_to === currentUser?.id) && (
                      <>
                        {activity.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => handleStatusChange(activity, 'in_progress')}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Start
                          </Button>
                        )}
                        {activity.status === 'in_progress' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleAddProgressUpdate(activity.id)}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              Add Progress
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleStatusChange(activity, 'completed')}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Complete
                            </Button>
                          </>
                        )}
                      </>
                    )}
                    {canDeleteActivity && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteActivity(activity.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Create Activity Modal */}
        {showAddForm && (
          <ActivityForm
            newActivity={newActivity}
            setNewActivity={setNewActivity}
            users={users}
            customers={customers}
            products={products}
            companySettings={companySettings}
            onSubmit={handleAddActivity}
            onCancel={() => setShowAddForm(false)}
          />
        )}

        {/* Status Update Modal */}
        {showStatusModal && (
          <ActivityStatusModal
            selectedActivity={selectedActivity}
            statusUpdateNote={statusUpdateNote}
            setStatusUpdateNote={setStatusUpdateNote}
            completionData={completionData}
            setCompletionData={setCompletionData}
            companySettings={companySettings}
            onConfirm={handleConfirmStatusUpdate}
            onCancel={() => {
              setShowStatusModal(false);
              setStatusUpdateNote('');
              setCompletionData({ invoice_number: '', work_order_no: '', total_amount: '', next_maintenance_date: '' });
              setSelectedActivity(null);
            }}
          />
        )}

        {/* Progress Update Modal */}
        {showProgressModal && (
          <ActivityProgressModal
            progressUpdate={progressUpdate}
            setProgressUpdate={setProgressUpdate}
            onConfirm={handleConfirmProgressUpdate}
            onCancel={() => setShowProgressModal(false)}
          />
        )}

        {/* Activity Details Modal */}
        {showDetailModal && selectedActivity && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="sticky top-0 bg-white z-10 border-b">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-2xl">{selectedActivity.title}</CardTitle>
                    <Badge className={`${getStatusBadge(selectedActivity.status)} mt-2`}>
                      {selectedActivity.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Description */}
                {selectedActivity.description && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Description</h4>
                    <p className="text-gray-600">{selectedActivity.description}</p>
                  </div>
                )}

                {/* Activity Information */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3">📋 Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <span className="ml-2 font-medium">{selectedActivity.activity_type || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Priority:</span>
                      <span className="ml-2 font-medium">{selectedActivity.priority || 'N/A'}</span>
                    </div>
                    {selectedActivity.serial_number && (
                      <div className="col-span-2">
                        <span className="text-gray-500">🔢 Product Serial Number:</span>
                        <span className="ml-2 font-semibold text-blue-600">{selectedActivity.serial_number}</span>
                      </div>
                    )}
                    <div className="col-span-2">
                      <span className="text-gray-500">📦 Products:</span>
                      <div className="ml-2 mt-1">
                        {selectedActivity.product_ids && selectedActivity.product_ids.length > 0
                          ? selectedActivity.product_ids.map((pid) => {
                              const product = products.find(p => p.id === pid);
                              return (
                                <div key={pid} className="mb-1">
                                  <span className="font-medium">{product?.name || pid}</span>
                                  {product?.serial_number && (
                                    <span className="ml-2 text-sm text-blue-600">
                                      (Serial: {product.serial_number})
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          : <span className="text-gray-400">No products selected</span>}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Created by:</span>
                      <span className="ml-2 font-medium text-blue-600">{getUserName(selectedActivity.created_by)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Assigned to:</span>
                      <span className="ml-2 font-medium">{getUserName(selectedActivity.assigned_to)}</span>
                    </div>
                    {selectedActivity.support_staff && (
                      <div>
                        <span className="text-gray-500">Support Staff:</span>
                        <span className="ml-2 font-medium">{getUserName(selectedActivity.support_staff)}</span>
                      </div>
                    )}
                    {selectedActivity.customer_id && (
                      <div>
                        <span className="text-gray-500">Customer:</span>
                        <span className="ml-2 font-medium text-cyan-600">
                          {customers.find(c => c.id === selectedActivity.customer_id)?.name || 'Unknown'}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Created:</span>
                      <span className="ml-2 font-medium">
                        {new Date(selectedActivity.created_at).toLocaleString()}
                      </span>
                    </div>
                    {selectedActivity.due_date && (
                      <div>
                        <span className="text-gray-500">Due Date:</span>
                        <span className="ml-2 font-medium">
                          {new Date(selectedActivity.due_date).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {selectedActivity.completion_date && (
                      <div>
                        <span className="text-gray-500">Completed:</span>
                        <span className="ml-2 font-medium text-green-600">
                          {new Date(selectedActivity.completion_date).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {selectedActivity.next_maintenance_date && (
                      <div className="col-span-2">
                        <span className="text-gray-500">🔧 Next Maintenance Due:</span>
                        <span className="ml-2 font-bold text-orange-600">
                          {formatDateDMY(selectedActivity.next_maintenance_date)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Completion Details */}
                {selectedActivity.status === 'completed' && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-700 mb-3">💰 Completion Details</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {selectedActivity.work_order_no && (
                        <div>
                          <span className="text-gray-600">Work Order:</span>
                          <span className="ml-2 font-medium">{selectedActivity.work_order_no}</span>
                        </div>
                      )}
                      {selectedActivity.invoice_number && (
                        <div>
                          <span className="text-gray-600">Invoice:</span>
                          <span className="ml-2 font-medium">{selectedActivity.invoice_number}</span>
                        </div>
                      )}
                      {selectedActivity.total_amount && (
                        <div className="col-span-2">
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="ml-2 font-bold text-green-700">
                            {formatAmount(selectedActivity.total_amount)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Financial Details */}
                {selectedActivity.status === 'completed' && (selectedActivity.invoice_number || selectedActivity.work_order_no || selectedActivity.total_amount) && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3">💰 Financial Details</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {selectedActivity.invoice_number && (
                        <div>
                          <span className="text-gray-500">Invoice #:</span>
                          <span className="ml-2 font-medium">{selectedActivity.invoice_number}</span>
                        </div>
                      )}
                      {selectedActivity.work_order_no && (
                        <div>
                          <span className="text-gray-500">Work Order #:</span>
                          <span className="ml-2 font-medium">{selectedActivity.work_order_no}</span>
                        </div>
                      )}
                      {selectedActivity.total_amount && (
                        <div>
                          <span className="text-gray-500">Total Amount:</span>
                          <span className="ml-2 font-medium text-green-600">
                            {formatAmount(parseFloat(selectedActivity.total_amount))}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Visit Photos — show existing & let creator / assignee
                    add more from the phone camera. Drops in regardless of
                    activity status. */}
                {(() => {
                  const canEditPhotos = canEditActivity && (
                    seesAllRecords ||
                    selectedActivity.created_by === currentUser?.id ||
                    selectedActivity.assigned_to === currentUser?.id
                  );
                  const photos = selectedActivity.photos || [];
                  if (!canEditPhotos && photos.length === 0) return null;
                  return (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-3">
                        📷 Visit Photos ({photos.length})
                      </h4>
                      {canEditPhotos ? (
                        <PhotoCapture
                          value={photos}
                          onChange={async (next) => {
                            // Optimistically update local view, then persist.
                            setSelectedActivity({ ...selectedActivity, photos: next });
                            try {
                              await api.put(`/activities/${selectedActivity.id}`, { photos: next });
                              setActivities((acts) => acts.map((a) =>
                                a.id === selectedActivity.id ? { ...a, photos: next } : a
                              ));
                            } catch (e) {
                              alert('Failed to save photo: ' + (e?.response?.data?.detail || e.message));
                              // Roll back the optimistic update on failure.
                              setSelectedActivity({ ...selectedActivity, photos });
                            }
                          }}
                          limit={5}
                          label=""
                          testId="activity-detail-photos"
                          sourcePreference="PROMPT"
                        />
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {photos.map((src, idx) => (
                            <a
                              key={idx}
                              href={src}
                              target="_blank"
                              rel="noreferrer"
                              className="w-20 h-20 rounded-lg overflow-hidden border border-slate-300 bg-slate-50 shadow-sm block"
                            >
                              <img src={src} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Progress History */}
                {selectedActivity.progress_updates && selectedActivity.progress_updates.length > 0 && (
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-gray-700">
                        📊 Progress History ({selectedActivity.progress_updates.length} updates)
                      </h4>
                      {/* Only creator or assignee can add progress */}
                      {selectedActivity.status === 'in_progress' && 
                       (selectedActivity.created_by === currentUser.id || selectedActivity.assigned_to === currentUser.id) && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setShowDetailModal(false);
                            handleAddProgressUpdate(selectedActivity.id);
                          }}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          + Add Progress
                        </Button>
                      )}
                    </div>
                    
                    {/* Info message for support users who can't add progress */}
                    {selectedActivity.status === 'in_progress' && 
                     selectedActivity.created_by !== currentUser.id && 
                     selectedActivity.assigned_to !== currentUser.id && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3 text-xs text-yellow-800">
                        ℹ️ Only the creator or assignee can add progress
                      </div>
                    )}
                    
                    <div className="space-y-3">
                      {selectedActivity.progress_updates.slice().reverse().map((update) => (
                        <div key={`progress-${update.timestamp}`} className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <p className="text-sm text-gray-700">{update.update}</p>
                            </div>
                            <span className="text-xs text-gray-500 ml-4 whitespace-nowrap">
                              {new Date(update.timestamp).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show "No Progress" for in_progress activities */}
                {selectedActivity.status === 'in_progress' && (!selectedActivity.progress_updates || selectedActivity.progress_updates.length === 0) && (
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-gray-700">📊 Progress History</h4>
                      {/* Only creator or assignee can add first progress */}
                      {(selectedActivity.created_by === currentUser.id || selectedActivity.assigned_to === currentUser.id) && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setShowDetailModal(false);
                            handleAddProgressUpdate(selectedActivity.id);
                          }}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          + Add First Progress
                        </Button>
                      )}
                    </div>
                    
                    {/* Message for creator/assignee with no progress yet */}
                    {(selectedActivity.created_by === currentUser.id || selectedActivity.assigned_to === currentUser.id) ? (
                      <div className="bg-gray-50 p-6 rounded-lg text-center">
                        <p className="text-gray-500">No progress updates yet</p>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                        ℹ️ Only the activity creator or assignee can add progress updates
                      </div>
                    )}
                  </div>
                )}

                {/* Status History */}
                {selectedActivity.status_history && selectedActivity.status_history.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3">
                      🔄 Status History ({selectedActivity.status_history.length} changes)
                    </h4>
                    <div className="space-y-2">
                      {selectedActivity.status_history.slice().reverse().map((history) => (
                        <div key={`status-${history.timestamp}-${history.status}`} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <Badge className={getStatusBadge(history.status)}>
                                {history.status.replace('_', ' ').toUpperCase()}
                              </Badge>
                              {history.notes && (
                                <p className="text-sm text-gray-600 mt-2">{history.notes}</p>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 ml-4 whitespace-nowrap">
                              {new Date(history.timestamp).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  {/* Only show edit/update buttons if user has edit permission AND
                      is admin / creator / assignee. */}
                  {canEditActivity && (seesAllRecords || selectedActivity.created_by === currentUser?.id || selectedActivity.assigned_to === currentUser?.id) && (
                    <>
                      {selectedActivity.status === 'pending' && (
                        <Button
                          onClick={() => {
                            setShowDetailModal(false);
                            handleStatusChange(selectedActivity, 'in_progress');
                          }}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Start Activity
                        </Button>
                      )}
                      {selectedActivity.status === 'in_progress' && (
                        <>
                          <Button
                            onClick={() => {
                              setShowDetailModal(false);
                              handleAddProgressUpdate(selectedActivity.id);
                            }}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            Add Progress
                          </Button>
                          <Button
                            onClick={() => {
                              setShowDetailModal(false);
                              handleStatusChange(selectedActivity, 'completed');
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Mark Complete
                          </Button>
                        </>
                      )}
                    </>
                  )}
                  
                  {/* Show message if user cannot edit */}
                  {!(canEditActivity && (seesAllRecords || selectedActivity.created_by === currentUser?.id || selectedActivity.assigned_to === currentUser?.id)) && (
                    <p className="text-sm text-gray-500 italic">
                      ℹ️ You don&apos;t have permission to edit this activity. Ask an admin to grant Activities → Edit, or to assign you to it.
                    </p>
                  )}
                  
                  {/* Reassign activity — admin/super_admin OR users with explicit edit rights */}
                  {(seesAllRecords || canEditActivity) && (
                    <Button
                      variant="outline"
                      onClick={() => handleEditAssignment(selectedActivity)}
                      className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    >
                      ✏️ Edit Assignment
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    onClick={() => setShowDetailModal(false)}
                  >
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      {/* Edit Assignment Modal (Admin Only) */}
      {showEditAssignmentModal && selectedActivity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>✏️ Edit Activity</span>
                <button
                  onClick={() => setShowEditAssignmentModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Activity:</strong> {selectedActivity.title}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Current Assignment:</strong> {getUserName(selectedActivity.assigned_to)}
                </p>
              </div>
              
              <div>
                <Label htmlFor="reassign-to">Reassign To</Label>
                <SearchableSelect
                  value={editAssignedTo}
                  onChange={(val) => setEditAssignedTo(val)}
                  options={supportUsers.map(user => ({ value: user.id, label: user.name }))}
                  placeholder="-- Unassigned --"
                />
              </div>

              <div>
                <Label htmlFor="edit-created-date">Created Date</Label>
                <input
                  id="edit-created-date"
                  type="date"
                  value={editCreatedDate}
                  onChange={(e) => setEditCreatedDate(e.target.value)}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  data-testid="edit-activity-created-date"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Original: {selectedActivity.created_at ? new Date(selectedActivity.created_at).toLocaleDateString() : '—'}
                </p>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleUpdateAssignment}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={
                    editAssignedTo === (selectedActivity.assigned_to || '') &&
                    editCreatedDate === String(selectedActivity.created_at || '').slice(0, 10)
                  }
                >
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowEditAssignmentModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default Activities;
